import {gsap} from "gsap/all";
import $ from "jquery";

// The speed dial of screen 2: the gradient arc around the dial and the readout
// in its middle. The arc grows from its cyan tip on the right around the top
// and down to the bottom left, empty at rest and whole at top speed. The
// reveal is a mask stroked along the arc, shortened by its dash, and the white
// dot rides the head of that reveal.
//
// Every state comes from the JSON, either content.game.speed.start for the top
// of the descent or the speed object of one of the gauge states of a sequence
// action:
//   value     speed in mph, counted up or down to rather than swapped
//   duration  travel time in seconds, overriding whatever the game asks for
//   ease      GSAP ease of the travel, "none" for a steady pace
//
// States always name the speed in mph and the readout writes it thousands
// separated (10,500). Under ten it counts to a tenth instead, which is where
// the last of the descent plays out (5.5). The unit is static markup, the
// readout never changes it.
export class SpeedGauge {

  // How the dial spreads the speed along its arc. The descent loses almost all
  // of it in the first moments (10,500 mph down to 1,600) and then takes the
  // rest of the run to crawl to a stop through numbers a hundred times smaller
  // (559, then 5.5), so read straight off the value the arc would empty in one
  // move and never be seen to change again.
  //
  // Instead the level is the speed raised to a power below one, which stretches
  // the small end of the range and compresses the big one, so every step of the
  // run reads as a comparable move along the arc. Whatever the exponent, 0 is
  // exactly empty and the max exactly full: only the spacing between them
  // changes, lower stretching the small end further. Tunable from the JSON, see
  // EDL.getSpeedCurve.
  static CURVE = 0.2;

  // Speed under which the readout writes a decimal, and how many it writes
  // there. Above it the numbers are big enough that a decimal is only noise.
  static DECIMALS_UNDER = 10;
  static DECIMALS = 1;

  constructor(el) {
    this.$el = $(el);

    this.$arc = this.$el.find(".speed-gauge-mask-arc");
    this.$dot = this.$el.find(".speed-gauge-dot");
    this.$value = this.$el.find(".speed-gauge-readout .value");

    // The dot is drawn at the far end of the arc, so it is moved by the offset
    // between that resting spot and wherever the reveal currently ends. Going
    // through a transform leaves the authored cx/cy alone.
    this.arc = this.$arc.get(0) || null;
    this.dotOrigin = this.$dot.length
      ? {x: parseFloat(this.$dot.attr("cx")), y: parseFloat(this.$dot.attr("cy"))}
      : null;

    // The dash drawing the arc is as long as the arc itself, with a gap just
    // as long behind it, so the offset runs from its whole length (nothing
    // shown) to zero (all of it). The length is measured once here since the
    // path never changes, and the SVG ships with a dash showing none of it.
    this.arcLength = this.arc ? this.arc.getTotalLength() : 0;
    this.$arc.attr("stroke-dasharray", `${this.arcLength} ${this.arcLength}`);

    this.level = 0;
    this.value = 0;
    this.levelTween = null;
    this.valueTween = null;

    this.renderLevel();
  }

  // Moves the dial to a state, over `duration` seconds. Pass 0 to snap, which
  // is what a reset and the start of a run do. Every field is optional, so a
  // partial state only touches what it names and leaves the rest alone.
  setState(state, duration = 1.2) {
    if (!state) return;

    // A state travelled over a long stretch (a whole action, say) reads better
    // at a steady pace, so it can ask for its own ease.
    const ease = state.ease || "power2.inOut";

    this.setValue(state.value, duration, ease);
    this.setLevel(this.getLevelForValue(state.value), duration, ease);
  }

  // How much of the arc a speed fills, on the curve above.
  getLevelForValue(value) {
    if (value === undefined) return undefined;

    const max = app.getSpeedMaxState()?.value || 1;
    const curve = app.getSpeedCurve ? app.getSpeedCurve() : SpeedGauge.CURVE;

    return Math.pow(Math.max(0, value) / max, curve);
  }

  // How much of the arc shows, 0 to 1, already through the curve above.
  setLevel(percent, duration, ease = "power2.inOut") {
    // console.log(percent, duration, ease,)
    if (percent === undefined) return;

    if (this.levelTween) {
      this.levelTween.kill();
      this.levelTween = null;
    }

    const level = Math.max(0, Math.min(1, percent));

    if (duration <= 0) {
      this.level = level;
      this.renderLevel();
      return;
    }

    this.levelTween = gsap.to(this, {
      level,
      duration,
      ease,
      onUpdate: () => this.renderLevel()
    });
  }

  // Counts the readout to its new value instead of swapping it, the way the
  // altitude of the ground distance gauge does.
  setValue(value, duration, ease = "power2.inOut") {
    if (value === undefined) return;

    if (this.valueTween) {
      this.valueTween.kill();
      this.valueTween = null;
    }

    if (duration <= 0) {
      this.value = value;
      this.renderValue();
      return;
    }

    this.valueTween = gsap.to(this, {
      value,
      duration,
      ease,
      onUpdate: () => this.renderValue()
    });
  }

  renderLevel() {
    this.$arc.attr("stroke-dashoffset", this.arcLength * (1 - this.level));
    this.renderDot();
  }

  // Parks the dot on the head of the reveal, which is the same point along the
  // arc that the dash exposes, so it reads as the tip of the gradient rather
  // than a mark sitting at the end of the dial.
  renderDot() {
    if (!this.arc || !this.dotOrigin || !this.arcLength) return;

    const point = this.arc.getPointAtLength(this.arcLength * this.level);

    this.$dot.attr("transform",
      `translate(${point.x - this.dotOrigin.x} ${point.y - this.dotOrigin.y})`);
  }

  // Miles an hour, thousands separated so the readout reads at a glance rather
  // than as a run of digits (10,500 MPH). A decimal only shows once the ship is
  // slow enough for it to mean something (5.5 MPH), so the count picks it up on
  // its way down rather than carrying it through the big numbers.
  renderValue() {
    const decimals = this.value < SpeedGauge.DECIMALS_UNDER ? SpeedGauge.DECIMALS : 0;

    this.$value.text(this.value.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }));
  }

  // Back to the top of the descent, with nothing animating.
  reset(startState) {
    if (this.levelTween) {
      this.levelTween.kill();
      this.levelTween = null;
    }
    if (this.valueTween) {
      this.valueTween.kill();
      this.valueTween = null;
    }

    this.setState(startState, 0);
  }
}
